import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Alert } from '@mui/material';

import {
  AlphaFieldRenderer,
  collectErrors,
  isWideField,
  toSubmitValues,
} from './AlphaFieldRenderer';
import { clearCardDraft, readCardDraft, writeCardDraft } from './cardDraft';
import { focusMayMove } from './cardFocusGuard';
import {
  changedKeys,
  getPath,
  isRecord,
  setPath,
  visibilityOf,
} from './cardModel';
import {
  BlockedNote,
  CardBadge,
  CardEyebrow,
  CardFooter,
  CardFrame,
  CardHeader,
  CardRecipients,
  CardRoot,
  CardSentence,
  FieldCell,
  FieldGrid,
  FooterSpacer,
  PrimaryButton,
  SecondaryButton,
} from './cardStyles';
import {
  AlphaCard,
  CardHandlers,
  CardServerError,
  FieldErrors,
  FormField,
} from './cardTypes';
import { smsSegments } from './smsSegments';
import {
  ANOTHER_REQUEST_TEXT,
  CardFailureAlert,
  SUBMIT_FAILED_TEXT,
  addOnce,
  belongsTo,
  useCardActions,
  useCardFocus,
} from './useCardActions';

export interface AlphaEditablePreviewProps extends CardHandlers {
  token: string;
  card: AlphaCard;
  serverError?: CardServerError;
  submitting?: boolean;
  cancelling?: boolean;
  blocked?: boolean;
  blockedMessage?: string;
  cancelBlocked?: boolean;
}

const RECHECK_HINT = 'Alpha will re-check this before sending';
const SUBMIT_FAILED = SUBMIT_FAILED_TEXT.confirm;
const CANCEL_FAILED = "Couldn't cancel. Try again.";
const SMS_TOOLS = ['send_sms'];

const initialValuesFor = (
  fields: FormField[],
  values: Record<string, unknown>,
): Record<string, unknown> =>
  fields.reduce<Record<string, unknown>>(
    (current, field) =>
      getPath(current, field.key) === undefined && field.default != null
        ? setPath(current, field.key, field.default)
        : current,
    values,
  );

const titleFor = (card: AlphaCard): string => {
  if (card.title != null && card.title !== '') {
    return card.title;
  }
  const words = card.toolName.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const buttonLabel = (toolName: string, edited: boolean): string => {
  if (toolName.startsWith('send_')) {
    return edited ? 'Send edited message' : 'Send';
  }
  return edited ? 'Save changes' : 'Save';
};

const smsCount = (text: string): string => {
  const count = smsSegments(text);
  const unit = count.chars === 1 ? 'character' : 'characters';
  return `${count.chars} ${unit} · ${count.segments} SMS`;
};

const recipientNames = (sample: unknown): string[] =>
  Array.isArray(sample)
    ? sample.filter((name): name is string => typeof name === 'string')
    : [];

export const AlphaEditablePreview = ({
  token,
  card,
  serverError,
  submitting,
  cancelling: cancellingElsewhere,
  blocked = false,
  blockedMessage = ANOTHER_REQUEST_TEXT,
  cancelBlocked = false,
  loadOptions,
  onSubmit,
  onCancel,
}: AlphaEditablePreviewProps): JSX.Element => {
  const fields = useMemo(
    () => card.editableFields ?? [],
    [card.editableFields],
  );
  const preview = isRecord(card.preview) ? card.preview : {};
  const initial = useMemo(
    () => initialValuesFor(fields, isRecord(card.values) ? card.values : {}),
    [fields, card.values],
  );
  const [values, setValues] = useState<Record<string, unknown>>(
    () => readCardDraft(token)?.values ?? initial,
  );
  const [attempted, setAttempted] = useState(false);
  const [touched, setTouched] = useState<string[]>([]);
  const interacted = useRef(false);
  const rootRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLSpanElement>(null);
  const titleId = `alpha-preview-${useId().replace(/:/g, '')}`;
  const blockedId = `${titleId}-blocked`;
  const { requestFocus, holdFocus } = useCardFocus(rootRef, titleRef);
  const actions = useCardActions({
    token,
    fields,
    serverError,
    values,
    submitting,
    cancellingElsewhere,
    onSubmit,
    onCancel,
    submitFailedMessage: SUBMIT_FAILED,
    cancelFailedMessage: CANCEL_FAILED,
    holdFocus,
    onServerFailure: (fieldErrors, initialFailure) => {
      if (!initialFailure && Object.keys(fieldErrors).length > 0) {
        requestFocus('invalid');
      }
    },
  });
  const { busy, cancelling, failure, serverFieldErrors } = actions;

  useEffect(() => {
    if (interacted.current) {
      writeCardDraft(token, { values });
    }
  }, [token, values]);

  const changed = changedKeys(initial, values, fields);
  const sentence = typeof preview.message === 'string' ? preview.message : '';
  const recipientCount =
    typeof preview.recipient_count === 'number'
      ? preview.recipient_count
      : undefined;
  const names = recipientNames(preview.recipients_sample);
  const visibility = visibilityOf(fields, values);
  const localErrors = collectErrors(fields, values, '', visibility);
  const visibleLocal: FieldErrors = attempted
    ? localErrors
    : Object.fromEntries(
        Object.entries(localErrors).filter(([key]) =>
          touched.some(touchedKey => belongsTo(key, touchedKey)),
        ),
      );
  const shownErrors = { ...serverFieldErrors, ...visibleLocal };

  const hintFor = (field: FormField): string | undefined => {
    if (field.target === true && changed.includes(field.key)) {
      return RECHECK_HINT;
    }
    if (SMS_TOOLS.includes(card.toolName) && field.type === 'textarea') {
      const value = getPath(values, field.key);
      return smsCount(typeof value === 'string' ? value : '');
    }
    return undefined;
  };

  const changeField =
    (field: FormField) =>
    (next: unknown): void => {
      interacted.current = true;
      setValues(previous => setPath(previous, field.key, next));
      actions.markEdited(field.key);
    };

  const touchField = (field: FormField) => (): void => {
    setTouched(previous => addOnce(previous, field.key));
  };

  const reset = (): void => {
    if (focusMayMove(rootRef.current)) {
      titleRef.current?.focus();
    }
    interacted.current = false;
    clearCardDraft(token);
    setValues(initial);
    setAttempted(false);
    setTouched([]);
  };

  const cancel = (): void => {
    if (!cancelBlocked) {
      actions.cancel();
    }
  };

  const send = (): void => {
    if (blocked) {
      return;
    }
    if (Object.keys(localErrors).length > 0) {
      setAttempted(true);
      requestFocus('invalid');
      return;
    }
    actions.submit(toSubmitValues(fields, values));
  };

  const label = buttonLabel(card.toolName, changed.length > 0);

  return (
    <CardFrame>
      <CardRoot ref={rootRef} aria-labelledby={titleId} aria-busy={busy}>
        <CardHeader>
          <CardEyebrow
            ref={titleRef}
            id={titleId}
            tabIndex={-1}
            data-alpha-card-title=""
          >
            {titleFor(card)}
          </CardEyebrow>
          {sentence !== '' && <CardSentence>{sentence}</CardSentence>}
          {preview.created_disabled === true && (
            <CardBadge>Will be created switched off</CardBadge>
          )}
          {recipientCount != null && (
            <CardRecipients>
              {`${recipientCount} recipient${recipientCount === 1 ? '' : 's'}`}
              {names.length > 0 && <span>{` — ${names.join(', ')}`}</span>}
            </CardRecipients>
          )}
        </CardHeader>
        <CardFailureAlert
          failure={failure}
          busy={busy}
          canRetrySubmit={!blocked}
          onRetrySubmit={send}
          onRetryCancel={cancel}
        />
        <FieldGrid>
          {fields
            .filter(field => visibility.field(field))
            .map(field => (
              <FieldCell key={field.key} wide={isWideField(field)}>
                <AlphaFieldRenderer
                  field={field}
                  value={getPath(values, field.key)}
                  scope={visibility}
                  errors={shownErrors}
                  hint={hintFor(field)}
                  disabled={busy}
                  loadOptions={loadOptions}
                  prefill={isRecord(card.values) ? card.values : undefined}
                  onChange={changeField(field)}
                  onBlur={touchField(field)}
                />
              </FieldCell>
            ))}
        </FieldGrid>
        {card.notice != null && card.notice !== '' && (
          <Alert severity="info" role="status">
            {card.notice}
          </Alert>
        )}
        {(blocked || cancelBlocked) && (
          <BlockedNote id={blockedId}>{blockedMessage}</BlockedNote>
        )}
        <CardFooter>
          <SecondaryButton
            type="button"
            disabled={busy || cancelling}
            aria-disabled={cancelBlocked || undefined}
            aria-describedby={cancelBlocked ? blockedId : undefined}
            onClick={cancel}
          >
            Cancel
          </SecondaryButton>
          <FooterSpacer />
          <SecondaryButton
            type="button"
            disabled={busy || changed.length === 0}
            onClick={reset}
          >
            Reset
          </SecondaryButton>
          <PrimaryButton
            type="button"
            disabled={busy || cancelling}
            aria-disabled={blocked || undefined}
            aria-describedby={blocked ? blockedId : undefined}
            onClick={send}
          >
            {busy ? `${label}…` : label}
          </PrimaryButton>
        </CardFooter>
      </CardRoot>
    </CardFrame>
  );
};
